/**
 * LeadOpsFacetFilters — reusable corridor + truck selects for ops lists.
 */

import React, { useMemo } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
} from "@mui/material";
import {
  collectCorridorFilterOptions,
  collectTruckFilterOptions,
  LEAD_FILTER_ALL,
} from "../services/leadListFilters.js";

/**
 * @param {object} props
 * @param {object[]} [props.leads] leads used to build option lists/counts
 * @param {string} [props.corridor]
 * @param {string} [props.truck]
 * @param {(value: string) => void} [props.onCorridorChange]
 * @param {(value: string) => void} [props.onTruckChange]
 * @param {() => void} [props.onClear]
 */
const LeadOpsFacetFilters = ({
  leads = [],
  corridor = LEAD_FILTER_ALL,
  truck = LEAD_FILTER_ALL,
  onCorridorChange,
  onTruckChange,
  onClear,
}) => {
  const corridorOptions = useMemo(
    () => collectCorridorFilterOptions(leads),
    [leads]
  );
  const truckOptions = useMemo(() => collectTruckFilterOptions(leads), [leads]);

  const hasActive = Boolean(corridor || truck);

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap={1.5}
      mb={2}
      data-testid="lead-ops-facet-filters"
    >
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="lead-filter-corridor-label">Corridor</InputLabel>
        <Select
          labelId="lead-filter-corridor-label"
          label="Corridor"
          value={corridor || LEAD_FILTER_ALL}
          onChange={(e) => onCorridorChange?.(e.target.value)}
        >
          <MenuItem value={LEAD_FILTER_ALL}>All corridors</MenuItem>
          {corridorOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label} ({opt.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="lead-filter-truck-label">Truck</InputLabel>
        <Select
          labelId="lead-filter-truck-label"
          label="Truck"
          value={truck || LEAD_FILTER_ALL}
          onChange={(e) => onTruckChange?.(e.target.value)}
        >
          <MenuItem value={LEAD_FILTER_ALL}>All trucks</MenuItem>
          {truckOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label} ({opt.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {hasActive && onClear && (
        <Button size="small" onClick={onClear}>
          Clear filters
        </Button>
      )}
    </Box>
  );
};

export default LeadOpsFacetFilters;
